<template>
  <div
    class="md:flex-1 rounded-2xl bg-white-005 p-4 flex flex-col mb-6 md:mb-0"
    style="height: 392px"
  >
    <div class="flex justify-between items-center mb-4">
      <div class="font-bold text-sm lg:text-xl ml-2">Top Collections</div>
      <ShareBtn tip="Top Collections" class="mr-2"></ShareBtn>
    </div>
    <div
      class="text-xs flex h-8 items-center mb-3"
      style="color: rgba(255, 255, 255, 0.7); background: #1e2022"
    >
      <div class="pl-4" style="width: 50%">Project</div>
      <div class="pl-4" style="width: 26%">
        <Sort v-model="state.sortValue" :index="0">DAOs</Sort>
      </div>
      <div class="flex justify-end pr-4" style="width: 24%">
        <Sort v-model="state.sortValue" :index="1"> Collected Value </Sort>
      </div>
    </div>

    <div class="flex-1 min-h-0 w-full text-xs">
      <Skeletor class="w-full h-full rounded" v-if="loading"></Skeletor>
      <div v-else class="h-full overflow-y-scroll" ref="scroller">
        <div class="space-y-2">
          <div v-for="(item, i) in state.list" :key="i">
            <div>
              <div>
                <div
                  class="
                    h-12
                    flex
                    items-center
                    hover:bg-white-005
                    cursor-pointer
                    rounded
                  "
                >
                  <div class="flex pl-4 pr-2 items-center" style="width: 50%">
                    <UiImg
                      class="
                        h-8
                        w-8
                        overflow-hidden
                        rounded-full
                        flex-shrink-0
                        mr-2
                      "
                      :src="
                        item.tokenLogo ||
                        require('@/assets/icons/nftnologo.avif')
                      "
                    ></UiImg>
                    <div class="font-bold">
                      {{ item.tokenName }}
                    </div>
                  </div>
                  <div class="flex items-center pl-4" style="width: 26%">
                    <tippy
                      allowHTML
                      :arrow="false"
                      interactive
                      :trigger="isLargeScreen ? undefined : 'click'"
                      :popperOptions="{ escapeWithReference: false }"
                      :maxWidth="306"
                      :onShow="onTipShow"
                    >
                      <div class="flex items-center">
                        <UiImg
                          v-for="(dao, j) in item.daoInfos.slice(
                            0,
                            isLargeScreen ? 3 : 1,
                          )"
                          :key="j"
                          class="h-8 w-8 overflow-hidden rounded-full"
                          :src="
                            dao.logo || require('@/assets/icons/nftnologo.avif')
                          "
                          :style="{
                            marginLeft: j != 0 ? -12 + 'px' : undefined,
                          }"
                        ></UiImg>

                        <div
                          class="w-2 h-2 rounded-full bg-gradientRed -ml-1"
                          v-if="item.daoInfos.length > (isLargeScreen ? 2 : 1)"
                        ></div>

                        <div
                          v-if="item.daoInfos.length > (isLargeScreen ? 2 : 1)"
                          class="font-bold"
                        >
                          +{{ item.daoInfos.length }}
                        </div>
                      </div>

                      <template #content>
                        <div class="flex flex-wrap items-center">
                          <router-link
                            :to="`/dao/${dao.daoPath}`"
                            v-for="(dao, j) in item.daoInfos"
                            :key="j"
                            class="p-2"
                          >
                            <UiImg
                              class="h-10 w-10 overflow-hidden rounded-full"
                              :src="
                                dao.logo ||
                                require('@/assets/icons/nftnologo.avif')
                              "
                          /></router-link>
                        </div>
                      </template>
                    </tippy>
                  </div>
                  <div class="font-bold text-right pr-4" style="width: 24%">
                    ${{ formatVal(item.totalAmount, { bit: 2, omit: true }) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { queryHomeCollectedRank } from '@/api/common';
import { useDesktop, useReqByBool } from '@/hooks';
import { computed, onMounted, reactive, watch, ref, inject } from 'vue';
import { Skeletor } from 'vue-skeletor';
import UiImg from '@/components/UiImg.vue';
import { formatVal } from '@/utils';
import ShareBtn from './ShareBtn.vue';
import Sort from '@/components/Sort.vue';

const curChain = inject('curChain');
const homeState = inject('homeState');
const isLargeScreen = useDesktop();

const state = reactive({
  list: [],
  sortValue: [-1, 0],
});

watch(
  () => JSON.stringify(state.sortValue),
  () => {
    const res = JSON.parse(JSON.stringify(state.list));

    if (state.sortValue[0] != -1) {
      res.sort((a, b) =>
        state.sortValue[0]
          ? a.daoInfos.length + -b.daoInfos.length
          : b.daoInfos.length - a.daoInfos.length,
      );
    }

    if (state.sortValue[1] != -1) {
      res.sort((a, b) =>
        state.sortValue[1]
          ? a.totalAmount - b.totalAmount
          : b.totalAmount - a.totalAmount,
      );
    }

    state.list = res;
  },
);

const scroller = ref(null);
const tipIns = ref(null);
const onTipShow = (ins) => {
  tipIns.value = ins;
};

watch(
  () => scroller.value,
  () => {
    if (scroller.value)
      scroller.value.addEventListener('scroll', () => {
        if (tipIns.value && tipIns.value.state.isVisible) {
          tipIns.value.hide();
          tipIns.value.reference.blur(); // remove focus from target (fix for touch)
        }
      });
  },
);

const { loadData, loading } = useReqByBool(async () => {
  const res = await queryHomeCollectedRank(
    curChain.value,
    homeState.filterStatus,
    true,
  );
  state.list = res.data.records;
});

watch(() => [curChain.value, homeState.filterStatus], loadData);

onMounted(() => {
  loadData();
});
</script>

<style lang="scss" scoped></style>
